import { useEffect, useState, useCallback, useRef } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Gauge, RefreshCw, Shield, Users, Zap, Clock, Save, Trash2, AlertTriangle, Infinity, Radio, Activity } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';

export default function RateLimitDashboardPage() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrideVal, setOverrideVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);

  // WebSocket for real-time updates
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    const wsUrl = baseUrl.replace(/^http/, 'ws') + '/api/ws/rate-limits';
    let ws;
    let reconnectTimer;
    const connect = () => {
      ws = new WebSocket(wsUrl);
      ws.onopen = () => { setWsConnected(true); wsRef.current = ws; };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'rate_limit_update') setLiveData(msg.data);
        } catch {}
      };
      ws.onclose = () => { setWsConnected(false); reconnectTimer = setTimeout(connect, 5000); };
      ws.onerror = () => ws.close();
    };
    connect();
    return () => { clearTimeout(reconnectTimer); ws?.close(); };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, overviewRes] = await Promise.all([
        api.get('/rate-limits/status'),
        api.get('/rate-limits/overview').catch(() => null),
      ]);
      setStatus(statusRes.data);
      if (overviewRes) {
        setOverview(overviewRes.data);
        if (overviewRes.data.custom_override?.requests_per_minute) {
          setOverrideVal(String(overviewRes.data.custom_override.requests_per_minute));
        }
      }
    } catch {
      toast.error('Failed to load rate limit data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSetOverride = async () => {
    const val = parseInt(overrideVal);
    if (!val || val < 10) { toast.error('Minimum 10 requests/minute'); return; }
    setSaving(true);
    try {
      await api.put('/rate-limits/override', { requests_per_minute: val });
      toast.success(`Rate limit set to ${val} req/min`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set override');
    } finally { setSaving(false); }
  };

  const handleDeleteOverride = async () => {
    setSaving(true);
    try {
      await api.delete('/rate-limits/override');
      toast.success('Custom override removed');
      setOverrideVal('');
      fetchData();
    } catch {
      toast.error('Failed to remove override');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    </DashboardLayout>
  );

  const isUnlimited = status?.is_unlimited;
  const usagePct = status?.user_limit > 0 ? Math.min(100, Math.round((status.current_usage / status.user_limit) * 100)) : 0;
  const roleColors = { admin: '#f97316', manager: '#3b82f6', operator: '#8b5cf6', cashier: '#10b981', super_admin: '#ef4444' };

  const userChartData = (overview?.users || [])
    .filter(u => u.current_usage > 0)
    .sort((a, b) => b.current_usage - a.current_usage)
    .slice(0, 10);

  const roleTiers = overview ? Object.entries(overview.role_multipliers || {}).map(([role, mult]) => ({
    role,
    multiplier: mult === -1 ? 'Unlimited' : `${Math.round(mult * 100)}%`,
    effective: mult === -1 ? 'Unlimited' : overview.plan_limit > 0 ? Math.max(10, Math.round(overview.plan_limit * mult)) : 'Unlimited',
  })) : [];

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-5xl mx-auto" data-testid="rate-limit-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-200/50">

                <Activity size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="rate-limit-title">Rate Limit Dashboard</h1>

                <p className="text-sm text-muted-foreground mt-0.5">API rate limiting dashboard</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">{t('rate_limit_subtitle') || 'Monitor and configure API usage limits'}</p>
          </div>
          <div className="flex gap-2 items-center">
            <Badge variant={wsConnected ? "default" : "secondary"} className={`text-xs gap-1 ${wsConnected ? 'bg-success' : ''}`} data-testid="ws-status">
              <Radio size={10} className={wsConnected ? 'animate-pulse' : ''} /> {wsConnected ? 'Live' : 'Offline'}
            </Badge>
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-rate-limit-btn">
              <RefreshCw size={14} className="mr-1" /> {t('refresh') || 'Refresh'}
            </Button>
          </div>
        </div>

        {/* Live Stats (WebSocket) */}
        {liveData && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="live-stats-grid">
            <Card className="border-success/30 bg-success/10 dark:bg-emerald-950/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-success font-medium">Active Users</p>
                <p className="text-2xl font-bold text-success">{liveData.active_users}</p>
              </CardContent>
            </Card>
            <Card className="border-info/30 bg-info/10 dark:bg-blue-950/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-info font-medium">User Requests</p>
                <p className="text-2xl font-bold text-info">{liveData.total_user_requests}</p>
              </CardContent>
            </Card>
            <Card className="border-purple-200 bg-purple-50/50 dark:bg-purple-950/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-purple-600 font-medium">Active IPs</p>
                <p className="text-2xl font-bold text-purple-700">{liveData.active_ips}</p>
              </CardContent>
            </Card>
            <Card className="border-warning/30 bg-warning/10 dark:bg-amber-950/20">
              <CardContent className="p-3 text-center">
                <p className="text-xs text-warning font-medium">IP Requests</p>
                <p className="text-2xl font-bold text-warning">{liveData.total_ip_requests}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Your Status Card */}
        <Card className="border-orange-100 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/20 dark:to-amber-950/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield size={16} className="text-orange-600" />
              {t('rate_limit_your_status') || 'Your Rate Limit Status'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-white/60 dark:bg-foreground/40 rounded-lg" data-testid="rl-plan">
                <p className="text-xs text-muted-foreground">{t('rate_limit_plan') || 'Plan'}</p>
                <p className="text-lg font-bold capitalize text-foreground dark:text-stone-200">{status?.plan || '—'}</p>
              </div>
              <div className="text-center p-3 bg-white/60 dark:bg-foreground/40 rounded-lg" data-testid="rl-role">
                <p className="text-xs text-muted-foreground">{t('rate_limit_role') || 'Role'}</p>
                <p className="text-lg font-bold capitalize text-foreground dark:text-stone-200">{status?.role || '—'}</p>
              </div>
              <div className="text-center p-3 bg-white/60 dark:bg-foreground/40 rounded-lg" data-testid="rl-limit">
                <p className="text-xs text-muted-foreground">{t('rate_limit_your_limit') || 'Your Limit'}</p>
                <p className="text-lg font-bold text-foreground dark:text-stone-200">
                  {isUnlimited ? <Infinity size={20} className="inline text-success" /> : `${status?.user_limit}/min`}
                </p>
              </div>
              <div className="text-center p-3 bg-white/60 dark:bg-foreground/40 rounded-lg" data-testid="rl-usage">
                <p className="text-xs text-muted-foreground">{t('rate_limit_current_usage') || 'Current Usage'}</p>
                <p className="text-lg font-bold text-foreground dark:text-stone-200">{status?.current_usage || 0}</p>
              </div>
            </div>
            {!isUnlimited && status?.user_limit > 0 && (
              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{status.current_usage} / {status.user_limit} requests</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> Resets in {status.reset_in_seconds}s</span>
                </div>
                <Progress
                  value={usagePct}
                  className={`h-2.5 ${usagePct > 80 ? '[&>div]:bg-error' : usagePct > 50 ? '[&>div]:bg-warning' : '[&>div]:bg-success'}`}
                  data-testid="rl-progress"
                />
              </div>
            )}
            {isUnlimited && (
              <div className="flex items-center gap-2 text-sm text-success bg-success/10 dark:bg-emerald-950/30 p-2 rounded" data-testid="rl-unlimited-badge">
                <Infinity size={14} /> Unlimited — No rate limit enforced for your account
              </div>
            )}
          </CardContent>
        </Card>

        {/* Admin: Tenant Overview */}
        {overview && (
          <>
            {/* Tier Reference */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap size={16} className="text-info" />
                  {t('rate_limit_tiers') || 'Rate Limit Tiers'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <div className="p-3 rounded-lg border bg-muted/50 dark:bg-foreground text-center">
                    <p className="text-xs text-muted-foreground">Plan Limit</p>
                    <p className="text-xl font-bold text-foreground dark:text-stone-200" data-testid="rl-plan-limit">
                      {overview.plan_limit === -1 ? 'Unlimited' : `${overview.plan_limit}/min`}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg border bg-muted/50 dark:bg-foreground text-center">
                    <p className="text-xs text-muted-foreground">IP Limit (Anonymous)</p>
                    <p className="text-xl font-bold text-foreground dark:text-stone-200" data-testid="rl-ip-limit">{overview.ip_limit}/min</p>
                  </div>
                  <div className="p-3 rounded-lg border bg-muted/50 dark:bg-foreground text-center">
                    <p className="text-xs text-muted-foreground">Window</p>
                    <p className="text-xl font-bold text-foreground dark:text-stone-200">{overview.window_seconds}s</p>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rl-tiers-table">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 font-medium">Role</th>
                        <th className="text-center py-2 font-medium">Multiplier</th>
                        <th className="text-right py-2 font-medium">Effective Limit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roleTiers.map(r => (
                        <tr key={r.role} className="border-b border-border dark:border-stone-800">
                          <td className="py-2 capitalize font-medium" style={{ color: roleColors[r.role] || '#6b7280' }}>{r.role.replace('_', ' ')}</td>
                          <td className="py-2 text-center">{r.multiplier}</td>
                          <td className="py-2 text-right font-mono">{typeof r.effective === 'number' ? `${r.effective}/min` : r.effective}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Custom Override */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle size={16} className="text-warning" />
                  {t('rate_limit_override') || 'Custom Rate Limit Override'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Override the plan default for your entire organization. This takes precedence over plan-based limits.
                </p>
                {overview.custom_override && (
                  <div className="flex items-center gap-2 text-sm bg-warning/10 dark:bg-amber-950/20 border border-warning/30 p-3 rounded-lg" data-testid="rl-active-override">
                    <AlertTriangle size={14} className="text-warning" />
                    Active override: <strong>{overview.custom_override.requests_per_minute} req/min</strong>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={10}
                    placeholder="e.g. 200"
                    value={overrideVal}
                    onChange={e => setOverrideVal(e.target.value)}
                    className="w-40"
                    data-testid="rl-override-input"
                  />
                  <span className="text-sm text-muted-foreground">req/min</span>
                  <Button size="sm" onClick={handleSetOverride} disabled={saving} data-testid="rl-override-save">
                    <Save size={14} className="mr-1" /> Set
                  </Button>
                  {overview.custom_override && (
                    <Button size="sm" variant="destructive" onClick={handleDeleteOverride} disabled={saving} data-testid="rl-override-delete">
                      <Trash2 size={14} className="mr-1" /> Remove
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Per-User Usage Chart */}
            {userChartData.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users size={16} className="text-purple-500" />
                    {t('rate_limit_top_users') || 'Top API Consumers (Current Window)'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64" data-testid="rl-usage-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userChartData} layout="vertical" margin={{ left: 80, right: 20 }}>
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                        <Tooltip formatter={(v) => [`${v} requests`, 'Usage']} />
                        <Bar dataKey="current_usage" radius={[0, 4, 4, 0]}>
                          {userChartData.map((entry, idx) => (
                            <Cell key={idx} fill={roleColors[entry.role] || '#6b7280'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Per-User Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('rate_limit_user_breakdown') || 'User Breakdown'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="rl-users-table">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 font-medium">User</th>
                        <th className="text-center py-2 font-medium">Role</th>
                        <th className="text-center py-2 font-medium">Limit</th>
                        <th className="text-center py-2 font-medium">Usage</th>
                        <th className="text-right py-2 font-medium">Remaining</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overview.users || []).map(u => {
                        const pct = u.limit > 0 ? Math.round((u.current_usage / u.limit) * 100) : 0;
                        return (
                          <tr key={u.user_id} className="border-b border-border dark:border-stone-800">
                            <td className="py-2 font-medium text-foreground dark:text-stone-200">{u.name}</td>
                            <td className="py-2 text-center">
                              <Badge variant="outline" className="text-xs capitalize" style={{ borderColor: roleColors[u.role] || '#6b7280', color: roleColors[u.role] || '#6b7280' }}>
                                {u.role}
                              </Badge>
                            </td>
                            <td className="py-2 text-center font-mono">{u.limit === -1 ? 'Unlimited' : u.limit}</td>
                            <td className="py-2 text-center">
                              <span className={pct > 80 ? 'text-error font-bold' : pct > 50 ? 'text-warning' : 'text-muted-foreground'}>
                                {u.current_usage}
                              </span>
                            </td>
                            <td className="py-2 text-right font-mono">{u.remaining === -1 ? '∞' : u.remaining}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
